import type { RichTextValue } from '@lighthouse/core'
import { nanoid } from '@lighthouse/tools'
import type { Editor, JSONContent } from '@tiptap/core'
import type { Transaction } from '@tiptap/pm/state'
import { clone } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { useApplicationContext, useSpaceQuotaContext } from '../../../../contexts'
import { RichTextEditor } from '../../../RichText'
import type { ToolbarProps } from '../../../RichText/Toolbar'
import { useTokenRefreshManager } from '../../../UploadManage'
import type { FieldBaseProps } from '../../types'

interface NotesFieldProps extends FieldBaseProps {
    value?: RichTextValue
}

const SCxContainer = styled.div<{ readOnly?: boolean }>`
    width: 100%;
    font-size: var(--font-size-normal);
    border-radius: 6px;
    background-color: ${({ readOnly }) => (readOnly ? 'transparent' : '#fff')};
`

const NotesField: React.FC<NotesFieldProps> = props => {
    const {
        readOnly,
        onBlur,
        onChange,
        value,
        config: { richTextUploadOptions, fieldPointer = '', placeholder, initialValue, breakPoint },
        record
    } = props
    const { appId } = useApplicationContext()
    const [key, setKey] = useState(`${record?.id}-${fieldPointer}`)
    const tokenRefreshManager = useTokenRefreshManager()
    const { quota } = useSpaceQuotaContext()

    useUpdateEffect(() => {
        setKey(nanoid())
    }, [fieldPointer, initialValue, record?.id])

    const config: ToolbarProps['config'] = useMemo(
        () => ({
            mode: 'all',
            placeholder,
            image: {
                uploadOptions: richTextUploadOptions,
                appId,
                tokenRefreshManager: tokenRefreshManager || undefined,
                maxSize: quota?.uploadFileLimitSize
            },
            video: {
                uploadOptions: richTextUploadOptions,
                appId,
                tokenRefreshManager: tokenRefreshManager || undefined,
                maxSize: quota?.uploadFileLimitSize
            }
        }),
        [appId, placeholder, richTextUploadOptions, tokenRefreshManager, quota?.uploadFileLimitSize]
    )

    const handleChange = useCallback(
        (json: JSONContent) => {
            onChange?.({ type: 'notes', value: json })
        },
        [onChange]
    )

    const handleBlur = useCallback(
        ({ editor }: { editor: Editor; event: FocusEvent; transaction: Transaction }) => {
            onBlur?.({ type: 'notes', value: clone(editor.getJSON()) })
        },
        [onBlur]
    )

    return (
        <SCxContainer
            data-input-type="notes"
            data-field-input
            data-field-active={false}
            data-field-disabled={Boolean(readOnly)}
            readOnly={readOnly}
        >
            <RichTextEditor
                minHeight={readOnly ? undefined : 200}
                styles={{
                    toolbarContainer: {
                        borderTopRightRadius: 6,
                        borderTopLeftRadius: 6
                    }
                }}
                placeholder={placeholder}
                key={key}
                config={config}
                readonly={readOnly}
                value={value}
                onChange={handleChange}
                onEditorBlur={handleBlur}
            />
        </SCxContainer>
    )
}

export default NotesField
